import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  RefreshCw,
  Save,
  Settings,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  Download
} from "lucide-react";
import api from "../api/api";
import { useAuth } from "../auth/AuthProvider";
import { useDialog } from "../components/DialogProvider";
import { PageHeader, PremiumCard } from "../components/ui";
import { useAppSettings } from "../settings/AppSettingsProvider";
import { formatDateTime } from "../utils/datetime";

const LS_THRESHOLD_DRAFT = "admin.settings.lowStockThreshold.draft";
const LS_NOTIF_DRAFT = "admin.settings.notifications.draft";
const LS_STAFF_USERNAME_DRAFT = "admin.settings.staff.username.draft";
const LS_STAFF_EMAIL_DRAFT = "admin.settings.staff.email.draft";
const LS_ADMIN_NEW_USERNAME_DRAFT = "admin.settings.admin.username.draft";

function safeJsonParse(s) {
  try {
    return JSON.parse(String(s || ""));
  } catch {
    return null;
  }
}

function normalizeTitle(v) {
  return String(v ?? "").replace(/\s+/g, " ").trim();
}

function normalizeMessage(v) {
  return String(v ?? "").trim();
}

function normalizeAudience(v) {
  const a = String(v || "staff").trim().toLowerCase();
  if (a === "staff" || a === "admin" || a === "all") return a;
  return "staff";
}

function normalizeThreshold(v) {
  const n = Math.trunc(Number(v));
  if (!Number.isFinite(n) || n <= 0) return "";
  return String(n);
}

export default function AdminSettings() {
  const auth = useAuth();
  const { toastPromise, confirm } = useDialog();
  const { settings, refresh, setLowStockThresholdLocal } = useAppSettings();

  // ===== Admin OTP flows (email verification & credential change) =====
  const [verifyEmailCode, setVerifyEmailCode] = useState("");
  const [sendingVerifyOtp, setSendingVerifyOtp] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
    const [adminEmailInput, setAdminEmailInput] = useState(() => String(auth.user?.email || ""));
    const [savingAdminEmail, setSavingAdminEmail] = useState(false);
  // Handler untuk update email admin
  const onUpdateAdminEmail = async (e) => {
    e.preventDefault();
    const email = adminEmailInput.trim();
    if (!email) return;
    setSavingAdminEmail(true);
    try {
      await toastPromise(
        api.put("/admin/email", { email }),
        {
          loading: "Menyimpan email…",
          success: "Email admin berhasil disimpan",
          error: (e) => e?.response?.data?.message || "Gagal menyimpan email",
        },
        { successTitle: "Sukses", errorTitle: "Error" }
      );
      await refresh();
    } finally {
      setSavingAdminEmail(false);
    }
  };

  const [adminNewUsername, setAdminNewUsername] = useState(() => {
    const draft = localStorage.getItem(LS_ADMIN_NEW_USERNAME_DRAFT);
    return draft != null ? String(draft) : "";
  });
  const [adminNewPassword, setAdminNewPassword] = useState("");
  const [adminOtpCode, setAdminOtpCode] = useState("");
  const [sendingChangeOtp, setSendingChangeOtp] = useState(false);
  const [updatingCredentials, setUpdatingCredentials] = useState(false);

  const [thresholdInput, setThresholdInput] = useState(() => {
    const draft = localStorage.getItem(LS_THRESHOLD_DRAFT);
    return draft != null && draft !== "" ? String(draft) : String(settings.lowStockThreshold || 5);
  });

  const [notifTitle, setNotifTitle] = useState(() => {
    const draft = safeJsonParse(localStorage.getItem(LS_NOTIF_DRAFT));
    return draft?.title ? String(draft.title) : "";
  });
  const [notifMessage, setNotifMessage] = useState(() => {
    const draft = safeJsonParse(localStorage.getItem(LS_NOTIF_DRAFT));
    return draft?.message ? String(draft.message) : "";
  });
  const [notifAudience, setNotifAudience] = useState(() => {
    const draft = safeJsonParse(localStorage.getItem(LS_NOTIF_DRAFT));
    return normalizeAudience(draft?.audience);
  });

  const [notifLoading, setNotifLoading] = useState(false);
  const [notifs, setNotifs] = useState([]);

  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);

  const [newUsername, setNewUsername] = useState(() => {
    const draft = localStorage.getItem(LS_STAFF_USERNAME_DRAFT);
    return draft != null ? String(draft) : "";
  });
  const [newEmail, setNewEmail] = useState(() => {
    const draft = localStorage.getItem(LS_STAFF_EMAIL_DRAFT);
    return draft != null ? String(draft) : "";
  });
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("staff");

  useEffect(() => {
    // Jangan override kalau user lagi ngetik dan ada draft.
    const draft = localStorage.getItem(LS_THRESHOLD_DRAFT);
    if (draft == null || draft === "") {
      setThresholdInput(String(settings.lowStockThreshold || 5));
    }
  }, [settings.lowStockThreshold]);

  // Autosave draft threshold (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(thresholdInput || "");
      if (!v) localStorage.removeItem(LS_THRESHOLD_DRAFT);
      else localStorage.setItem(LS_THRESHOLD_DRAFT, v);
    }, 300);
    return () => clearTimeout(t);
  }, [thresholdInput]);

  // Autosave draft notif (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      const payload = {
        title: String(notifTitle || ""),
        message: String(notifMessage || ""),
        audience: normalizeAudience(notifAudience),
      };
      const hasAny = payload.title.trim() || payload.message.trim();
      if (!hasAny) localStorage.removeItem(LS_NOTIF_DRAFT);
      else localStorage.setItem(LS_NOTIF_DRAFT, JSON.stringify(payload));
    }, 300);
    return () => clearTimeout(t);
  }, [notifTitle, notifMessage, notifAudience]);

  // Autosave draft username (do NOT store password)
  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(newUsername || "");
      if (!v) localStorage.removeItem(LS_STAFF_USERNAME_DRAFT);
      else localStorage.setItem(LS_STAFF_USERNAME_DRAFT, v);
    }, 300);
    return () => clearTimeout(t);
  }, [newUsername]);

  // Autosave draft staff email
  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(newEmail || "");
      if (!v) localStorage.removeItem(LS_STAFF_EMAIL_DRAFT);
      else localStorage.setItem(LS_STAFF_EMAIL_DRAFT, v);
    }, 300);
    return () => clearTimeout(t);
  }, [newEmail]);

  // Autosave draft admin new username (do NOT store password)
  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(adminNewUsername || "");
      if (!v) localStorage.removeItem(LS_ADMIN_NEW_USERNAME_DRAFT);
      else localStorage.setItem(LS_ADMIN_NEW_USERNAME_DRAFT, v);
    }, 300);
    return () => clearTimeout(t);
  }, [adminNewUsername]);

  const loadNotifs = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await api.get("/admin/notifications?limit=50");
      setNotifs(Array.isArray(res?.data) ? res.data : []);
    } finally {
      setNotifLoading(false);
    }
  }, []);

  const loadStaff = useCallback(async () => {
    setStaffLoading(true);
    try {
      const res = await api.get("/admin/users");
      setStaff(Array.isArray(res.data) ? res.data : []);
    } finally {
      setStaffLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    loadNotifs();
  }, [loadNotifs]);

  const parsedThreshold = useMemo(() => {
    const n = Math.trunc(Number(thresholdInput));
    return Number.isFinite(n) ? n : NaN;
  }, [thresholdInput]);

  const onSaveThreshold = async () => {
    await toastPromise(
      api.put("/admin/settings", { lowStockThreshold: parsedThreshold }),
      {
        loading: "Menyimpan threshold…",
        success: "Threshold berhasil disimpan",
        error: (e) => e?.response?.data?.message || "Gagal menyimpan threshold",
      },
      { successTitle: "Sukses", errorTitle: "Error" }
    );
    setLowStockThresholdLocal(parsedThreshold);
    await refresh();
    localStorage.removeItem(LS_THRESHOLD_DRAFT);
  };

  const canCreateNotif = useMemo(() => {
    const t = normalizeTitle(notifTitle);
    const m = normalizeMessage(notifMessage);
    if (!t || !m) return false;
    if (t.length > 200) return false;
    if (m.length > 4000) return false;
    return true;
  }, [notifTitle, notifMessage]);

  const onCreateNotif = async (e) => {
    e.preventDefault();

    const t = normalizeTitle(notifTitle);
    const m = normalizeMessage(notifMessage);
    const a = normalizeAudience(notifAudience);

    await toastPromise(
      api.post("/admin/notifications", { title: t, message: m, audience: a }),
      {
        loading: "Membuat pemberitahuan…",
        success: "Pemberitahuan dibuat",
        error: (e2) => e2?.response?.data?.message || "Gagal membuat pemberitahuan",
      },
      { successTitle: "Sukses", errorTitle: "Error" }
    );

    setNotifTitle("");
    setNotifMessage("");
    setNotifAudience("staff");
    localStorage.removeItem(LS_NOTIF_DRAFT);
    await loadNotifs();
  };

  const onDeleteNotif = async (row) => {
    const ok = await confirm(`Hapus pemberitahuan "${row.title}"?`, {
      title: "Konfirmasi",
      confirmText: "Hapus",
      cancelText: "Batal",
    });
    if (!ok) return;

    await toastPromise(
      api.delete(`/admin/notifications/${row.id}`),
      {
        loading: "Menghapus pemberitahuan…",
        success: "Pemberitahuan dihapus",
        error: (e2) => e2?.response?.data?.message || "Gagal menghapus pemberitahuan",
      },
      { successTitle: "Sukses", errorTitle: "Error" }
    );
    await loadNotifs();
  };

  const onCreateStaff = async (e) => {
    e.preventDefault();

    const username = newUsername.trim();
    const email = String(newEmail || "").trim();
    const password = newPassword;
    if (!username || !password) return;

    await toastPromise(
      api.post("/admin/users", { username, email: email || null, password, role: newRole }),
      {
        loading: "Membuat akun…",
        success: "Akun berhasil dibuat",
        error: (e2) => e2?.response?.data?.message || "Gagal membuat akun staff",
      },
      { successTitle: "Sukses", errorTitle: "Error" }
    );

    setNewUsername("");
    setNewEmail("");
    setNewPassword("");
    localStorage.removeItem(LS_STAFF_USERNAME_DRAFT);
    localStorage.removeItem(LS_STAFF_EMAIL_DRAFT);
    await loadStaff();
  };

  const onDeleteStaff = async (u) => {
    const ok = await confirm(
      `Hapus akun staff "${u.username}"?`,
      { title: "Konfirmasi", confirmText: "Hapus", cancelText: "Batal" }
    );
    if (!ok) return;

    await toastPromise(
      api.delete(`/admin/users/${u.id}`),
      {
        loading: "Menghapus akun…",
        success: "Akun dihapus",
        error: (e2) => e2?.response?.data?.message || "Gagal menghapus akun staff",
      },
      { successTitle: "Sukses", errorTitle: "Error" }
    );
    await loadStaff();
  };

  const saveDisabled = !Number.isFinite(parsedThreshold) || parsedThreshold <= 0;

  const isAdmin = auth.user?.role === "admin";
  const adminEmail = String(auth.user?.email || "").trim();
  const isEmailVerified = Number(auth.user?.email_verified) === 1;

  const onSendEmailVerificationOtp = async () => {
    setSendingVerifyOtp(true);
    try {
      await toastPromise(
        api.post("/auth/send-email-verification"),
        {
          loading: "Mengirim OTP verifikasi…",
          success: "OTP dikirim ke email admin",
          error: (e) => e?.response?.data?.message || "Gagal mengirim OTP",
        },
        { successTitle: "Sukses", errorTitle: "Error" }
      );
    } finally {
      setSendingVerifyOtp(false);
    }
  };

  const onVerifyAdminEmail = async (e) => {
    e?.preventDefault?.();
    const code = String(verifyEmailCode || "").trim();
    if (!code) return;

    setVerifyingEmail(true);
    try {
      await toastPromise(
        api.post("/auth/verify-email", { code }),
        {
          loading: "Memverifikasi email…",
          success: "Email berhasil diverifikasi",
          error: (e2) => e2?.response?.data?.message || "Gagal verifikasi email",
        },
        { successTitle: "Sukses", errorTitle: "Error" }
      );
      setVerifyEmailCode("");
      await auth.refreshMe?.();
    } finally {
      setVerifyingEmail(false);
    }
  };

  const onRequestChangeCredentialsOtp = async () => {
    setSendingChangeOtp(true);
    try {
      await toastPromise(
        api.post("/auth/request-change-credentials-otp"),
        {
          loading: "Mengirim OTP ubah akun…",
          success: "OTP dikirim ke email admin",
          error: (e) => e?.response?.data?.message || "Gagal mengirim OTP",
        },
        { successTitle: "Sukses", errorTitle: "Error" }
      );
    } finally {
      setSendingChangeOtp(false);
    }
  };

  const onUpdateAdminCredentials = async (e) => {
    e.preventDefault();

    const code = String(adminOtpCode || "").trim();
    const username = String(adminNewUsername || "").trim();
    const password = String(adminNewPassword || "");

    if (!code) return;
    if (!username && !password) return;

    const payload = { code };
    if (username) payload.username = username;
    if (password) payload.password = password;

    setUpdatingCredentials(true);
    try {
      await toastPromise(
        api.post("/auth/update-credentials", payload),
        {
          loading: "Menyimpan perubahan akun…",
          success: () => "Data berhasil diperbarui",
          error: (e2) => e2?.response?.data?.message || "Gagal update akun",
        },
        { successTitle: "Sukses", errorTitle: "Error" }
      );

      // Bersihkan input sensitif
      setAdminNewPassword("");
      setAdminOtpCode("");

      // Username di token tidak akan otomatis berubah sampai login ulang.
      if (username) {
        setAdminNewUsername("");
        localStorage.removeItem(LS_ADMIN_NEW_USERNAME_DRAFT);
      }

      const relog = await confirm("Perubahan tersimpan. Login ulang sekarang?", {
        title: "Akun diperbarui",
        confirmText: "Login ulang",
        cancelText: "Nanti",
      });
      if (relog) {
        await auth.logout();
      }
    } finally {
      setUpdatingCredentials(false);
    }
  };

  const [activeTab, setActiveTab] = useState("staff");

  const TABS = [
    { id: "staff", label: "Kelola Pengguna", icon: Users },
    { id: "settings", label: "Sistem & Notifikasi", icon: Bell },
    { id: "backup", label: "Backup & Ekspor", icon: Download },
    { id: "security", label: "Keamanan Admin", icon: ShieldCheck },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Administration"
        title="Admin Settings"
        description={`Login sebagai ${auth.user?.username || "-"}. Kelola email admin, akun staff, notifikasi, dan threshold stok.`}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3 mb-6">
        <div className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-500">Status Email</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-950">{isEmailVerified ? "Verified" : "Pending"}</div>
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-200 bg-sky-50 text-sky-700">
            <Bell className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-500">Pemberitahuan Aktif</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-950">{notifs.length}</div>
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700">
            <Users className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-500">Total Pengguna</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-950">{staff.length}</div>
          </div>
        </div>
      </div>

      <div className="flex space-x-1 rounded-xl bg-slate-100 p-1 mb-6 md:w-max">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
              activeTab === t.id
                ? "bg-white text-indigo-700 shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "staff" && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <PremiumCard className="p-4 sm:p-6 border-indigo-100 shadow-md">
            <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-4 mb-4">
              <div>
                <div className="flex items-center gap-2 text-lg font-bold text-slate-900">
                  <UserPlus className="h-5 w-5 text-indigo-600" />
                  Tambah Akun Pengguna
                </div>
                <div className="mt-1 text-sm text-slate-500">Buat akun untuk Staff, Kasir, atau Akuntan.</div>
              </div>
            </div>

            <form onSubmit={onCreateStaff} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 items-start">
              <div className="lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Username *</label>
                <input
                  className="input w-full text-sm bg-slate-50"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="e.g. jdoe"
                  autoComplete="off"
                />
              </div>
              <div className="lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Email (Opsional)</label>
                <input
                  className="input w-full text-sm bg-slate-50"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="jdoe@mail.com"
                  autoComplete="email"
                />
              </div>
              <div className="lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Role *</label>
                <select
                  className="select w-full text-sm bg-slate-50"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <option value="staff">Staff</option>
                  <option value="kasir">Kasir</option>
                  <option value="accountant">Akuntan</option>
                </select>
              </div>
              <div className="lg:col-span-1">
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Password *</label>
                <input
                  className="input w-full text-sm bg-slate-50"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  type="password"
                  autoComplete="new-password"
                />
              </div>
              <div className="lg:col-span-1 flex flex-col justify-end h-full pt-6 lg:pt-0">
                <button className="btn btn-primary w-full h-10 shadow-md" type="submit" disabled={!newUsername.trim() || !newPassword}>
                  <UserPlus className="h-4 w-4" />
                  Buat Akun
                </button>
              </div>
            </form>
          </PremiumCard>

          <PremiumCard className="p-4 sm:p-6 shadow-sm">
            <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
              <div>
                <div className="text-lg font-bold text-slate-900">Daftar Pengguna</div>
                <div className="text-sm text-slate-500">Kelola akun yang sudah terdaftar.</div>
              </div>
              <button className="btn btn-secondary text-sm bg-slate-100" onClick={loadStaff} disabled={staffLoading}>
                <RefreshCw className={"h-4 w-4 " + (staffLoading ? "animate-spin" : "")} />
                Refresh Data
              </button>
            </div>

            <div className="table-shell rounded-xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 font-semibold">ID</th>
                    <th className="px-4 py-3 font-semibold">Username</th>
                    <th className="px-4 py-3 font-semibold">Email</th>
                    <th className="px-4 py-3 font-semibold">Role</th>
                    <th className="px-4 py-3 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {staffLoading ? (
                    <tr>
                      <td className="px-4 py-8 text-center text-slate-500" colSpan={5}>
                        <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-slate-400" />
                        Memuat data...
                      </td>
                    </tr>
                  ) : staff.length ? (
                    staff.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-slate-500 font-mono text-xs">{u.id}</td>
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          {u.username}
                          {u.role === "admin" && <span className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">Admin</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.email || <span className="text-slate-400 italic">Tidak ada</span>}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            u.role === "accountant" ? "bg-purple-100 text-purple-700" :
                            u.role === "kasir" ? "bg-emerald-100 text-emerald-700" :
                            u.role === "staff" ? "bg-blue-100 text-blue-700" :
                            "bg-slate-100 text-slate-700"
                          }`}>
                            {u.role.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            className="inline-flex items-center gap-1 rounded bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-100 transition-colors disabled:opacity-50"
                            onClick={() => onDeleteStaff(u)}
                            disabled={u.role === "admin"}
                            title={u.role === "admin" ? "Admin tidak bisa dihapus dari sini" : "Hapus Pengguna"}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Hapus
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-4 py-8 text-center text-slate-500" colSpan={5}>Belum ada pengguna.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </PremiumCard>
        </div>
      )}

      {activeTab === "settings" && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <PremiumCard className="p-4 sm:p-6 shadow-sm">
            <div className="flex items-center gap-2 text-lg font-bold text-slate-900 mb-4 pb-4 border-b border-slate-100">
              <Settings className="h-5 w-5 text-indigo-600" />
              Pengaturan Sistem
            </div>
            <div className="grid gap-4 sm:grid-cols-2 items-end max-w-lg">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Ambang Batas Stok Menipis (Threshold)</label>
                <div className="text-xs text-slate-500 mb-2">Sistem akan memberi tahu jika stok produk menyentuh angka ini.</div>
                <input
                  className="input w-full bg-slate-50"
                  inputMode="numeric"
                  value={thresholdInput}
                  onChange={(e) => setThresholdInput(normalizeThreshold(e.target.value))}
                  placeholder="Contoh: 10"
                />
              </div>
              <button
                className="btn btn-primary w-full sm:w-auto shadow-sm"
                onClick={onSaveThreshold}
                disabled={saveDisabled}
              >
                <Save className="h-4 w-4" />
                Simpan Konfigurasi
              </button>
            </div>
          </PremiumCard>

          <div className="grid gap-6 lg:grid-cols-3">
            <PremiumCard className="p-4 sm:p-6 shadow-sm lg:col-span-1 h-fit">
              <div className="text-lg font-bold text-slate-900 mb-1">Kirim Pengumuman</div>
              <div className="text-sm text-slate-500 mb-5">Broadcast notifikasi ke semua staff atau admin.</div>
              <form onSubmit={onCreateNotif} className="grid gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-1">Judul</label>
                  <input
                    className="input w-full bg-slate-50"
                    value={notifTitle}
                    onChange={(e) => setNotifTitle(e.target.value)}
                    placeholder="Briefing pagi ini..."
                    maxLength={200}
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-1">Kirim Kepada</label>
                  <select
                    className="select w-full bg-slate-50"
                    value={notifAudience}
                    onChange={(e) => setNotifAudience(e.target.value)}
                  >
                    <option value="staff">Staff (Semua Karyawan)</option>
                    <option value="all">Global (Termasuk Admin)</option>
                    <option value="admin">Hanya Admin</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 block mb-1">Pesan Lengkap</label>
                  <textarea
                    className="input w-full min-h-[120px] bg-slate-50 py-3"
                    value={notifMessage}
                    onChange={(e) => setNotifMessage(e.target.value)}
                    placeholder="Detail pengumuman..."
                    maxLength={4000}
                  />
                </div>
                <button className="btn btn-primary w-full mt-2" type="submit" disabled={!canCreateNotif}>
                  <Bell className="h-4 w-4" />
                  Broadcast Pesan
                </button>
              </form>
            </PremiumCard>

            <PremiumCard className="p-4 sm:p-6 shadow-sm lg:col-span-2">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <div className="text-lg font-bold text-slate-900">Riwayat Pengumuman</div>
                  <div className="text-sm text-slate-500">Daftar notifikasi yang sudah mengudara.</div>
                </div>
                <button className="btn btn-secondary text-sm bg-slate-100" onClick={loadNotifs} disabled={notifLoading}>
                  <RefreshCw className={"h-4 w-4 " + (notifLoading ? "animate-spin" : "")} />
                  Refresh
                </button>
              </div>

              <div className="table-shell rounded-xl border border-slate-200 h-[500px] overflow-y-auto relative">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-slate-600 border-b border-slate-200 sticky top-0 z-10 shadow-sm">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Terkirim</th>
                      <th className="px-4 py-3 font-semibold w-1/2">Pesan</th>
                      <th className="px-4 py-3 font-semibold text-center">Audiens</th>
                      <th className="px-4 py-3 font-semibold text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {notifLoading ? (
                      <tr>
                        <td className="px-4 py-8 text-center text-slate-500" colSpan={4}>Memuat riwayat...</td>
                      </tr>
                    ) : notifs.length ? (
                      notifs.map((row) => (
                        <tr key={row.id} className="hover:bg-slate-50 align-top transition-colors">
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                            {formatDateTime(row.created_at) || "-"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-bold text-slate-900">{row.title}</div>
                            <div className="mt-1 text-slate-600 text-xs leading-relaxed">{row.message}</div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex rounded bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-600">
                              {row.audience}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              className="inline-flex items-center gap-1 rounded bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-600 hover:bg-rose-100 disabled:opacity-30"
                              onClick={() => onDeleteNotif(row)}
                              disabled={Number(row.active) !== 1}
                            >
                              <Trash2 className="h-3 w-3" />
                              Tarik
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="px-4 py-8 text-center text-slate-500" colSpan={4}>Belum ada riwayat pengumuman.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </PremiumCard>
          </div>
        </div>
      )}

      {activeTab === "backup" && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <PremiumCard className="p-4 sm:p-6 shadow-sm">
            <div className="flex items-center gap-2 text-lg font-bold text-slate-900 mb-4 pb-4 border-b border-slate-100">
              <Download className="h-5 w-5 text-indigo-600" />
              Manual Backup
            </div>
            <div className="text-sm text-slate-500 mb-4">Unduh data sistem (Produk, Transaksi, Stok, Log) pada rentang tanggal tertentu ke dalam file Excel.</div>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              const start = fd.get("startDate");
              const end = fd.get("endDate");
              const deliveryMethod = fd.get("deliveryMethod");
              if (!start || !end) return;

              try {
                if (deliveryMethod === 'email') {
                  const response = await api.post("/admin/backup/manual", { 
                    startDate: start, endDate: end, deliveryMethod: 'email' 
                  });
                  toastPromise(Promise.resolve(), { success: response.data?.message || "Backup berhasil dikirim ke email" });
                } else {
                  const response = await api.post("/admin/backup/manual", 
                    { startDate: start, endDate: end, deliveryMethod: 'download' },
                    { responseType: "blob" }
                  );

                  const blob = response.data;
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `Backup_${start}_to_${end}.xlsx`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);

                  toastPromise(Promise.resolve(), { success: "Backup berhasil diunduh" });
                }
              } catch (err) {
                let errMsg = "Gagal memproses backup";
                if (err.response && err.response.data && err.response.data instanceof Blob) {
                  const text = await err.response.data.text();
                  try {
                    const json = JSON.parse(text);
                    errMsg = json.message || errMsg;
                  } catch (e) {
                    errMsg = text || errMsg;
                  }
                } else if (err.response?.data?.message) {
                  errMsg = err.response.data.message;
                }
                toastPromise(Promise.reject(err), { error: () => errMsg });
              }
            }} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 items-end">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Tanggal Mulai</label>
                <input type="date" name="startDate" required className="input w-full bg-slate-50" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Tanggal Akhir</label>
                <input type="date" name="endDate" required className="input w-full bg-slate-50" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Metode Ekspor</label>
                <select name="deliveryMethod" className="select w-full bg-slate-50">
                  <option value="download">Unduh Langsung</option>
                  <option value="email">Kirim ke Email Admin</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary w-full shadow-sm">
                <Download className="h-4 w-4" />
                Proses Backup
              </button>
            </form>
          </PremiumCard>

          <PremiumCard className="p-4 sm:p-6 shadow-sm">
            <div className="flex items-center gap-2 text-lg font-bold text-slate-900 mb-4 pb-4 border-b border-slate-100">
              <RefreshCw className="h-5 w-5 text-indigo-600" />
              Jadwal Backup Otomatis
            </div>
            <div className="text-sm text-slate-500 mb-4">Atur kapan backup otomatis (ke email admin) akan dijalankan setiap minggunya.</div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const fd = new FormData(e.target);
              const day = fd.get("day");
              const time = fd.get("time");
              
              await toastPromise(
                api.put("/admin/backup/schedule", { day, time }),
                {
                  loading: "Menyimpan jadwal...",
                  success: "Jadwal backup otomatis tersimpan",
                  error: (err) => err?.response?.data?.message || "Gagal menyimpan jadwal"
                },
                { successTitle: "Sukses", errorTitle: "Error" }
              );
            }} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-end">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Hari</label>
                <select name="day" className="select w-full bg-slate-50" defaultValue="0">
                  <option value="0">Minggu</option>
                  <option value="1">Senin</option>
                  <option value="2">Selasa</option>
                  <option value="3">Rabu</option>
                  <option value="4">Kamis</option>
                  <option value="5">Jumat</option>
                  <option value="6">Sabtu</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Jam (Waktu Server)</label>
                <input type="time" name="time" defaultValue="02:00" required className="input w-full bg-slate-50" />
              </div>
              <button type="submit" className="btn btn-secondary w-full shadow-sm">
                <Save className="h-4 w-4" />
                Simpan Jadwal
              </button>
            </form>
          </PremiumCard>
        </div>
      )}

      {activeTab === "security" && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          {!isAdmin ? (
            <PremiumCard className="p-6 border-amber-200 bg-amber-50">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-lg mb-2">
                <ShieldCheck className="h-6 w-6" />
                Akses Ditolak
              </div>
              <p className="text-amber-700">Tab keamanan ini dikhususkan hanya untuk Administrator utama.</p>
            </PremiumCard>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              <PremiumCard className="p-5 sm:p-7 border-indigo-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-6 opacity-10 pointer-events-none">
                  <ShieldCheck className="h-32 w-32 text-indigo-600" />
                </div>
                <div className="flex items-center gap-3 text-xl font-bold text-slate-900 mb-1 relative z-10">
                  Autentikasi Email Utama
                </div>
                <div className="text-sm text-slate-600 mb-6 relative z-10">
                  Email ini digunakan untuk pemulihan akun dan OTP pergantian kata sandi admin.
                </div>

                <div className={`mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider shadow-sm border ${isEmailVerified ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'} relative z-10`}>
                  {isEmailVerified ? <><CheckCircle2 className="h-3.5 w-3.5" /> Terverifikasi</> : <><AlertCircle className="h-3.5 w-3.5" /> Menunggu Verifikasi</>}
                </div>

                <div className="space-y-5 relative z-10">
                  <form onSubmit={onUpdateAdminEmail} className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <label className="text-sm font-semibold text-slate-800 block mb-2">Alamat Email Terdaftar</label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        className="input w-full bg-white"
                        type="email"
                        value={adminEmailInput}
                        onChange={e => setAdminEmailInput(e.target.value)}
                        placeholder="admin@perusahaan.com"
                      />
                      <button className="btn btn-primary whitespace-nowrap" type="submit" disabled={savingAdminEmail || !adminEmailInput.trim() || adminEmailInput.trim() === adminEmail}>
                        {savingAdminEmail ? "Menyimpan…" : "Simpan Perubahan"}
                      </button>
                    </div>
                  </form>

                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Verifikasi dengan OTP</div>
                        <div className="text-xs text-slate-500">Kode OTP 6 digit akan dikirimkan ke email terdaftar.</div>
                      </div>
                      <button className="btn btn-secondary bg-white shadow-sm text-xs" onClick={onSendEmailVerificationOtp} disabled={sendingVerifyOtp || !adminEmail}>
                        {sendingVerifyOtp ? "Mengirim..." : "Kirim OTP Verifikasi"}
                      </button>
                    </div>
                    <form onSubmit={onVerifyAdminEmail} className="flex flex-col sm:flex-row gap-3 mt-4 pt-4 border-t border-slate-200 border-dashed">
                      <input
                        className="input w-full sm:max-w-[200px] bg-white text-center font-mono tracking-widest font-bold text-slate-800"
                        inputMode="numeric"
                        value={verifyEmailCode}
                        onChange={(e) => setVerifyEmailCode(e.target.value)}
                        placeholder="• • • • • •"
                        maxLength={6}
                      />
                      <button className="btn btn-primary sm:flex-1" type="submit" disabled={!verifyEmailCode.trim() || verifyingEmail}>
                        {verifyingEmail ? "Memproses..." : "Konfirmasi Kode"}
                      </button>
                    </form>
                  </div>
                </div>
              </PremiumCard>

              <PremiumCard className="p-5 sm:p-7 shadow-sm">
                <div className="text-xl font-bold text-slate-900 mb-1">Ganti Kredensial (Root)</div>
                <div className="text-sm text-slate-600 mb-6">Ubah akses rahasia (username dan password admin). Membutuhkan OTP dari email.</div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 mb-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="text-sm font-medium text-slate-700">Langkah 1: Dapatkan otorisasi</div>
                    <button className="btn btn-secondary bg-white shadow-sm text-xs" type="button" onClick={onRequestChangeCredentialsOtp} disabled={sendingChangeOtp}>
                      {sendingChangeOtp ? "Mengirim..." : "Minta OTP Keamanan"}
                    </button>
                  </div>
                </div>

                <form onSubmit={onUpdateAdminCredentials} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 block mb-1">Username Baru (Opsional)</label>
                      <input
                        className="input w-full bg-slate-50"
                        value={adminNewUsername}
                        onChange={(e) => setAdminNewUsername(e.target.value)}
                        placeholder={auth.user?.username || "Admin Baru"}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 block mb-1">Password Baru (Opsional)</label>
                      <input
                        className="input w-full bg-slate-50"
                        value={adminNewPassword}
                        onChange={(e) => setAdminNewPassword(e.target.value)}
                        type="password"
                        placeholder="••••••••"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <label className="text-xs font-semibold text-rose-600 block mb-1 uppercase tracking-wide">Kode OTP Otorisasi *</label>
                    <div className="flex flex-col sm:flex-row gap-3 items-center">
                      <input
                        className="input w-full sm:max-w-[200px] bg-rose-50 border-rose-200 text-rose-700 font-mono tracking-widest font-bold text-center"
                        inputMode="numeric"
                        value={adminOtpCode}
                        onChange={(e) => setAdminOtpCode(e.target.value)}
                        placeholder="• • • • • •"
                        maxLength={6}
                      />
                      <button
                        className="btn btn-primary bg-slate-900 hover:bg-slate-800 text-white w-full sm:flex-1 shadow-md"
                        type="submit"
                        disabled={
                          updatingCredentials ||
                          !adminOtpCode.trim() ||
                          (!String(adminNewUsername || "").trim() && !String(adminNewPassword || ""))
                        }
                      >
                        {updatingCredentials ? "Menyimpan Keamanan…" : "Terapkan Perubahan"}
                      </button>
                    </div>
                    <div className="mt-3 text-xs text-slate-500 text-center sm:text-left">
                      *Aplikasi akan meminta Anda login ulang jika username atau password diubah.
                    </div>
                  </div>
                </form>
              </PremiumCard>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
